'use client'

/**
 * /embed/size-calculator — the refrigerator size calculator as a widget
 * (FRIDGE-356). Same three inputs and the SAME calculation as
 * /tools/refrigerator-size-calculator: both call
 * lib/tools/refrigerator-size.ts, so the widget cannot drift from the tool.
 * Records nothing the visitor chooses (no capture in this component).
 */
import { useMemo, useState } from 'react'
import {
  COOKING_FREQUENCY_OPTIONS,
  HOUSEHOLD_SIZE_OPTIONS,
  SHOPPING_FREQUENCY_OPTIONS,
  REFRIGERATOR_SIZE_DEFAULTS,
  calculateRefrigeratorSize,
} from '@/lib/tools/refrigerator-size'

const SELECT_CLASS =
  'w-full px-3 py-2.5 text-[14px] font-semibold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white text-gray-900'
const LABEL_CLASS = 'block text-[10px] font-bold uppercase tracking-[0.12em] text-gray-700 mb-1'

export function SizeCalculatorWidget() {
  const [householdSize, setHouseholdSize] = useState<string>(REFRIGERATOR_SIZE_DEFAULTS.householdSize)
  const [cookingFrequency, setCookingFrequency] = useState<string>(REFRIGERATOR_SIZE_DEFAULTS.cookingFrequency)
  const [shoppingFrequency, setShoppingFrequency] = useState<string>(REFRIGERATOR_SIZE_DEFAULTS.shoppingFrequency)

  const result = useMemo(
    () => calculateRefrigeratorSize({ householdSize, cookingFrequency, shoppingFrequency }),
    [householdSize, cookingFrequency, shoppingFrequency]
  )

  return (
    <div className="grid gap-4 @min-[420px]:grid-cols-2 @min-[420px]:items-stretch">
      <div className="space-y-3">
        <div>
          <label htmlFor="embed-size-household" className="block">
            <span className={LABEL_CLASS}>Household size</span>
            <select
              id="embed-size-household"
              value={householdSize}
              onChange={(e) => setHouseholdSize(e.target.value)}
              className={SELECT_CLASS}
            >
              {HOUSEHOLD_SIZE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div>
          <label htmlFor="embed-size-cooking" className="block">
            <span className={LABEL_CLASS}>Cooking frequency</span>
            <select
              id="embed-size-cooking"
              value={cookingFrequency}
              onChange={(e) => setCookingFrequency(e.target.value)}
              className={SELECT_CLASS}
            >
              {COOKING_FREQUENCY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div>
          <label htmlFor="embed-size-shopping" className="block">
            <span className={LABEL_CLASS}>Grocery shopping frequency</span>
            <select
              id="embed-size-shopping"
              value={shoppingFrequency}
              onChange={(e) => setShoppingFrequency(e.target.value)}
              className={SELECT_CLASS}
            >
              {SHOPPING_FREQUENCY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div
        className="rounded-xl p-4 text-white flex flex-col justify-between"
        style={{ background: 'linear-gradient(135deg, #0469f0 0%, #0053c4 100%)' }}
        aria-live="polite"
      >
        <p className="m-0 text-[10px] font-bold uppercase tracking-[0.14em] text-white/70">
          Recommended capacity
        </p>
        <p className="m-0 mt-1 font-black leading-none tracking-[-0.04em] text-[44px] tabular-nums">
          {result?.recommendedSize ?? 0}
          <span className="ml-1.5 text-[16px] font-black tracking-tight text-white/60">cu ft</span>
        </p>
        <p className="m-0 mt-2 text-[12px] text-white/85 leading-snug">
          {result?.recommendation ?? 'Enter your household details.'}
        </p>
        <div className="grid grid-cols-2 gap-2 mt-3">
          <div className="rounded-lg p-2.5 bg-white/10 border border-white/15">
            <p className="m-0 text-[9px] uppercase tracking-[0.14em] text-white/60 font-bold">Min size</p>
            <p className="m-0 text-[16px] font-black tracking-tight tabular-nums">
              {result?.minSize ?? '—'} cu ft
            </p>
          </div>
          <div className="rounded-lg p-2.5 bg-white/10 border border-white/15">
            <p className="m-0 text-[9px] uppercase tracking-[0.14em] text-white/60 font-bold">Max size</p>
            <p className="m-0 text-[16px] font-black tracking-tight tabular-nums">
              {result?.maxSize ?? '—'} cu ft
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
